import React from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { useState, useEffect } from 'react';
import apiClient from '../../../shared/api/axiosConfig';
import { deleteRoom } from '../../../shared/api/room';
import '../styles/RoomAdmin.css';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ElegantSidebar from '../../../shared/components/layout/ElegantSidebar';
import ScreenSettings from '../../admin-screen/components/ScreenSettings';
import type { ScreenDisplaySettings } from '../../admin-screen/components/ScreenSettings';
import { getBaseUrl, getApiBaseUrl } from '../../../shared/config/urls';
import {
  Heart,
  QrCode,
  Monitor,
  Shield,
  Plus,
  Home,
  ExternalLink,
  List,
  Calendar,
  Menu,
  X,
  Trash2,
  AlertTriangle,
  FileText,
  Settings
} from 'lucide-react';

type RoomResponse = {
  roomid: string;
  msg: string;
  coupleName?: string;
};

type UserRoom = {
  roomid: string;
  createdAt: string;
  coupleName?: string;
};

type UserRoomsResponse = {
  rooms: UserRoom[];
};

type Screen = {
  ret: boolean | string;
  msg: string;
};

const RoomAdmin = () => {
  const urlBase = getBaseUrl();
  const urlRoom = `${getApiBaseUrl()}/api/room`;
  const urlScreen = `${getApiBaseUrl()}/api/picture/screen`;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // roomIdを複数のソースから取得する関数
  const getRoomIdFromSources = (): string => {
    // 1. location.stateから取得
    const roomIdFromState = location.state?.roomid;
    if (roomIdFromState) {
      return roomIdFromState;
    }

    // 2. URLクエリパラメータから取得
    const roomIdFromQuery = searchParams.get('roomid');
    if (roomIdFromQuery) {
      return roomIdFromQuery;
    }

    // 3. sessionStorageから取得
    const roomIdFromSession = sessionStorage.getItem('roomid');
    if (roomIdFromSession) {
      return roomIdFromSession;
    }

    return '';
  };

  const [roomResponse, setRoomResponse] = useState<RoomResponse>();
  const [screen, setScreen] = useState<Screen>();
  const [roomId, setRoomId] = useState<string>('');
  const [moderationMsg, setModerationMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userRooms, setUserRooms] = useState<UserRoom[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState<boolean>(false);
  const [coupleName, setCoupleName] = useState<string>('');
  const [showRoomNameModal, setShowRoomNameModal] = useState<boolean>(false);
  const [modalRoomName, setModalRoomName] = useState<string>('');

  // 削除機能用のstate
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTargetRoom, setDeleteTargetRoom] = useState<UserRoom | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');

  // サイドバーのレスポンシブ状態管理
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // スクリーン設定モーダル管理
  const [showScreenSettings, setShowScreenSettings] = useState(false);

  // roomIdを設定すると同時にsessionStorageにも保存する関数
  const updateRoomId = (newRoomId: string) => {
    setRoomId(newRoomId);
    if (newRoomId) {
      sessionStorage.setItem('roomid', newRoomId);
    } else {
      sessionStorage.removeItem('roomid');
    }
  };

  // roomId初期化のuseEffect
  useEffect(() => {
    const initialRoomId = getRoomIdFromSources();
    if (initialRoomId) {
      updateRoomId(initialRoomId);
      setRoomResponse({
        roomid: initialRoomId,
        msg: "既存のルームを引き継ぎました"
      });
    }

    // ユーザーのルーム一覧を取得
    fetchUserRooms();
  }, []); // 依存配列を空にして初回のみ実行

  // location.stateやURLパラメータの変更を監視
  useEffect(() => {
    const currentRoomId = getRoomIdFromSources();
    if (currentRoomId && currentRoomId !== roomId) {
      updateRoomId(currentRoomId);
      setRoomResponse({
        roomid: currentRoomId,
        msg: "ルームを切り替えました"
      });
    }
  }, [location.state, searchParams, roomId]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 1024;

      setIsDesktop(newIsDesktop);

      // 画面サイズが変わった時のサイドバー状態の調整
      if (newIsDesktop && !isDesktop) {
        // モバイル → デスクトップ: サイドバーを閉じる（デスクトップでは常時表示）
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        // デスクトップ → モバイル: サイドバーを閉じる
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isDesktop]);

      const fetchUserRooms = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
  
      setIsLoadingRooms(true);
      try {
        const apiBaseUrl = getApiBaseUrl();
        console.log('RoomAdmin: getApiBaseUrl() returns:', apiBaseUrl);
        const response = await apiClient.get(`${apiBaseUrl}/api/room/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRooms(response.data.rooms || []);    } catch (error) {
      console.error('ルーム一覧取得失敗', error);
    } finally {
      setIsLoadingRooms(false);
    }
  };

  const createRoom = async (roomName: string) => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("ログインしてからルームを作成してください。");
      return;
    }

    if (!roomName.trim()) {
      alert("部屋の名前を入力してください。");
      return;
    }

    if (roomName.trim().length > 50) {
      alert("部屋の名前は50文字以内で入力してください。");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post(urlRoom, { coupleName: roomName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoomResponse(response.data);
      updateRoomId(response.data.roomid); // updateRoomIdを使用
      setCoupleName(roomName.trim()); // 作成成功時に表示用に保存

      // ユーザーのルーム一覧を再取得
      fetchUserRooms();
    } catch (error) {
      console.error('ルーム作成失敗', error);
      alert('ルーム作成に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRoomClick = () => {
    setModalRoomName('');
    setShowRoomNameModal(true);
  };

  const handleModalConfirm = () => {
    if (!modalRoomName.trim()) {
      alert("部屋の名前を入力してください。");
      return;
    }

    if (modalRoomName.trim().length > 50) {
      alert("部屋の名前は50文字以内で入力してください。");
      return;
    }

    setShowRoomNameModal(false);
    createRoom(modalRoomName.trim());
  };

  const handleModalCancel = () => {
    setShowRoomNameModal(false);
    setModalRoomName('');
  };

  const handleModalKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleModalConfirm();
    } else if (e.key === 'Escape') {
      handleModalCancel();
    }
  };

  const handlePrintQROnly = () => {
    const canvas = document.querySelector('.qr-code-frame canvas') as HTMLCanvasElement;
    if (!canvas) {
      alert('QRコードがまだ描画されていません');
      return;
    }

    // QRコード画像として取得
    const qrImageUrl = canvas.toDataURL('image/png');
    const qrURL = `${urlBase}/user/menu?roomid=${roomResponse?.roomid ?? ''}`;

    // 印刷用の新しいウィンドウを開く
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) {
      alert('ポップアップブロックを無効にしてください');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>QRコード印刷</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=Noto+Serif+JP:wght@300;400;500&display=swap');

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Noto Serif JP', 'Playfair Display', serif;
              background: linear-gradient(135deg, #f8f6f3 0%, #ede8e1 100%);
              color: #3a342d;
              min-height: 100vh;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 40px 20px;
            }

            .wedding-container {
              background: #ffffff;
              border-radius: 20px;
              padding: 60px 50px;
              box-shadow:
                0 20px 60px rgba(58, 52, 45, 0.15),
                0 8px 25px rgba(58, 52, 45, 0.08);
              border: 2px solid #f0ebe6;
              max-width: 500px;
              width: 100%;
              text-align: center;
              position: relative;
              overflow: hidden;
            }

            .wedding-container::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 4px;
              background: linear-gradient(90deg, #d4af37, #f4e4bc, #d4af37);
            }

            .wedding-title {
              font-family: 'Playfair Display', serif;
              font-size: 32px;
              font-weight: 600;
              color: #2d1b69;
              margin-bottom: 12px;
              letter-spacing: 1px;
            }

            .wedding-subtitle {
              font-size: 16px;
              color: #8b7355;
              margin-bottom: 40px;
              font-weight: 300;
              letter-spacing: 0.5px;
            }

            .qr-section {
              margin: 40px 0;
              position: relative;
            }

            .qr-frame {
              position: relative;
              display: inline-block;
              padding: 25px;
              background: #ffffff;
              border-radius: 15px;
              box-shadow:
                inset 0 0 0 2px #d4af37,
                0 8px 25px rgba(212, 175, 55, 0.2);
              margin-bottom: 30px;
            }

            .qr-frame::before {
              content: '';
              position: absolute;
              top: -2px;
              left: -2px;
              right: -2px;
              bottom: -2px;
              background: linear-gradient(45deg, #d4af37, #f4e4bc, #d4af37);
              border-radius: 17px;
              z-index: -1;
            }

            .qr-img {
              width: 200px;
              height: 200px;
              border-radius: 8px;
              display: block;
            }

            .decorative-elements {
              position: absolute;
              top: 15px;
              left: 50%;
              transform: translateX(-50%);
              display: flex;
              gap: 8px;
            }

            .decorative-dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background: #d4af37;
            }

            .access-instructions {
              background: #f8f6f3;
              border-radius: 12px;
              padding: 25px;
              margin: 30px 0;
              border-left: 4px solid #d4af37;
            }

            .instruction-title {
              font-size: 18px;
              font-weight: 500;
              color: #3a342d;
              margin-bottom: 15px;
              font-family: 'Playfair Display', serif;
            }

            .instruction-text {
              font-size: 14px;
              color: #6b5d4f;
              line-height: 1.6;
              margin-bottom: 8px;
            }

            .url-display {
              background: #ffffff;
              border: 1px solid #e0d5c7;
              border-radius: 8px;
              padding: 15px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              color: #555;
              word-break: break-all;
              margin-top: 15px;
            }

            .footer-message {
              margin-top: 40px;
              font-size: 14px;
              color: #8b7355;
              font-style: italic;
              border-top: 1px solid #e0d5c7;
              padding-top: 20px;
            }

            .ornamental-border {
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 25px 0;
              gap: 15px;
            }

            .border-line {
              flex: 1;
              height: 1px;
              background: linear-gradient(90deg, transparent, #d4af37, transparent);
            }

            .border-symbol {
              color: #d4af37;
              font-size: 16px;
            }

            @media print {
              body {
                background: white;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }

              .wedding-container {
                box-shadow: none;
                border: 1px solid #ddd;
              }
            }
          </style>
        </head>
        <body>
          <div class="wedding-container">
            <div class="decorative-elements">
              <div class="decorative-dot"></div>
              <div class="decorative-dot"></div>
              <div class="decorative-dot"></div>
            </div>

            <h1 class="wedding-title">Photo Memorial</h1>
            <p class="wedding-subtitle">Wedding Guest Access</p>

            <div class="ornamental-border">
              <div class="border-line"></div>
              <span class="border-symbol">❈</span>
              <div class="border-line"></div>
            </div>

            <div class="qr-section">
              <div class="qr-frame">
                <img class="qr-img" src="${qrImageUrl}" alt="Wedding QR Code" />
              </div>
            </div>

            <div class="access-instructions">
              <div class="instruction-title">ご利用方法</div>
              <div class="instruction-text">1. スマートフォンのカメラでQRコードをスキャン</div>
              <div class="instruction-text">2. 表示されたリンクをタップ</div>
              <div class="instruction-text">3. 素敵な写真をアップロード・共有</div>

            </div>

            <div class="ornamental-border">
              <div class="border-line"></div>
              <span class="border-symbol">❈</span>
              <div class="border-line"></div>
            </div>

          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // 印刷後にウィンドウを閉じる
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const enterScreen = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("ログインしてからスクリーンを表示してください。");
      return;
    }

    try {
      // スクリーン表示前に画像リスト状態をリセット（設定を確実に反映させるため）
      try {
        await axios.post(`${getApiBaseUrl()}/api/picture/resetScreenState?roomId=${roomId}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('[RoomAdmin] スクリーン状態をリセットしました');
      } catch (resetError) {
        console.warn('[RoomAdmin] スクリーン状態リセットに失敗:', resetError);
      }

      const response = await axios.get<Screen>(`${urlScreen}?roomId=${roomId}`, {
        withCredentials: true, // クッキー利用が必要な場合は残す
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setScreen(response.data);

      if (response.data.ret === "true") {
        window.open(`/admin/screen?roomid=${roomId}`, '_blank');
      } else if (response.data.ret === "false") {
        alert(`ルームID[${roomId}]は存在しません`);
      } else {
        alert("サーバーでエラーが発生しています");
      }
    } catch (error) {
      console.error('GETリクエストが失敗しました', error);
      console.error(token, error);
      alert('Exception!');
    }
  };

  const startModeration = () => {
    if (!roomId) {
      alert("先にルームを作成してください。");
      return;
    }

    window.open(`/admin/gallery?roomid=${roomId}`, '_blank');
  };

  const selectRoom = (selectedRoomId: string) => {
    const selectedRoom = userRooms.find(room => room.roomid === selectedRoomId);
    updateRoomId(selectedRoomId); // updateRoomIdを使用
    setRoomResponse({ roomid: selectedRoomId, msg: "過去のルームを選択しました" });
    setCoupleName(selectedRoom?.coupleName || ''); // 部屋名を設定
    setModerationMsg('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // サイドバートグル関数
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // ルーム削除処理
  const handleDeleteRoom = async (roomIdToDelete: string): Promise<boolean> => {
    setIsDeleting(true);
    setDeleteError(''); // エラーをクリア

    try {
      await deleteRoom(roomIdToDelete);

      // stateから削除されたルームを除外
      const updatedRooms = userRooms.filter(room => room.roomid !== roomIdToDelete);
      setUserRooms(updatedRooms);

      // 現在選択中のルームが削除された場合、または削除後にルームが0個になる場合の処理
      if (roomId === roomIdToDelete || updatedRooms.length === 0) {
        updateRoomId(''); // sessionStorageからも削除
        setRoomResponse(undefined); // QRコード表示をクリア
        setCoupleName('');
      }

      console.log(`ルーム削除成功: ${roomIdToDelete}`);
      return true; // 成功を返す

    } catch (error: any) {
      console.error('ルーム削除失敗', error);
      const errorMessage = error.message || 'ルーム削除に失敗しました';
      setDeleteError(errorMessage); // UIにエラーメッセージを設定
      return false; // 失敗を返す
    } finally {
      setIsDeleting(false);
    }
  };

  // 削除確認ハンドラー
  const handleDeleteRoomClick = (room: UserRoom) => {
    setDeleteTargetRoom(room);
    setShowDeleteModal(true);
    setDeleteError(''); // モーダルを開く際にエラーをクリア
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetRoom) return;

    const success = await handleDeleteRoom(deleteTargetRoom.roomid);

    if (success) {
      // 削除成功時のみモーダルを閉じる
      setShowDeleteModal(false);
      setDeleteTargetRoom(null);
      alert('ルームを削除しました');
    }
    // 失敗時はモーダルを開いたままにして、エラーメッセージを表示
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteTargetRoom(null);
    setDeleteError(''); // キャンセル時にエラーをクリア
  };

  // サイドバー外クリックで閉じる
  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-area');
    if (!printContent) return;

    const printWindow = window.open('', '', 'width=600,height=800');
    if (printWindow) {
      printWindow.document.write('<html><head><title>印刷</title>');
      printWindow.document.write('<style>body { font-family: sans-serif; text-align: center; } img { margin-top: 20px; }</style>');
      printWindow.document.write('</head><body >');
      printWindow.document.write(printContent.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleCreateQRPaper = () => {
    if (!roomResponse?.roomid) {
      alert("先にルームを作成してください。");
      return;
    }

    // QRコード用紙作成画面（デザイン選択）へ遷移
    navigate('/admin/qr-paper/design', {
      state: {
        roomid: roomResponse.roomid,
        qrUrl: `${urlBase}/user/menu?roomid=${roomResponse.roomid}`,
        coupleName: coupleName
      }
    });
  };

  const handleOpenScreenSettings = () => {
    if (!roomId) {
      alert("先にルームを選択してください。");
      return;
    }
    setShowScreenSettings(true);
  };

  const handleScreenSettingsChanged = (settings: ScreenDisplaySettings) => {
    console.log('スクリーン設定が更新されました:', settings);
  };

  return (
    <div className="wedding-admin-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}

      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>

      <div className={`wedding-main-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
        {/* ヘッダーセクション */}
        <div className="wedding-header">
          <div className="wedding-header-decoration">
          </div>
          
          {/* 右上設定ボタン */}
          <button
            onClick={handleOpenScreenSettings}
            disabled={!roomId.trim()}
            className="wedding-button tertiary-button screen-settings-top-button"
            title={roomId ? "スクリーン表示設定" : "先にルームを選択してください"}
          >
            <Settings size={18} />
            表示設定
          </button>
          
          <h1 className="wedding-title">
            Photo Memorial
          </h1>
          <p className="wedding-subtitle">Wedding Room Management System</p>
        </div>

        {/* メインコンテンツグリッド */}
        <div className="wedding-content-grid">

          {/* ルーム作成セクション */}
          <div className="wedding-card main-card">
            <div className="card-header">
              <div className="roomadmin-card-icon">
                <Home size={24} />
              </div>
              <h2 className="card-title">新しいルームを作成</h2>
              <p className="card-description">写真を共有できる専用ルームを作成します</p>
            </div>

            {/* 入力欄を削除 - ボタンクリック時にモーダルで入力 */}

            <button
              onClick={handleCreateRoomClick}
              disabled={isLoading}
              className="wedding-button primary-button"
            >
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <Plus size={20} />
                  ルーム作成
                </>
              )}
            </button>

            {roomResponse?.roomid && (
              <div className="room-info-card">
                <div className="room-info-header">
                  <QrCode size={20} className="qr-icon" />
                  <span>ルーム情報</span>
                </div>
                <div className="room-id-display">
                  <label>ルームID</label>
                  <div className="room-id-value">{roomResponse.roomid}</div>
                </div>
                {coupleName && (
                  <div className="couple-name-display">
                    <label>部屋の名前</label>
                    <div className="couple-name-value">{coupleName}</div>
                  </div>
                )}
                <div className="success-message">
                  <Heart size={16} />
                  {roomResponse.msg}
                </div>
              </div>
            )}
          </div>

          {/* ルーム選択セクション */}
          {userRooms.length > 0 && (
            <div className="wedding-card room-selection-card">
              <div className="card-header">
                <div className="roomadmin-card-icon">
                  <List size={24} />
                </div>
                <h2 className="card-title">過去のルーム選択</h2>
                <p className="card-description">作成済みのルームから選択して管理します</p>
              </div>

              <div className="room-selection-container">
                {isLoadingRooms ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <span>ルーム一覧を読み込み中...</span>
                  </div>
                ) : (
                  <div className="room-list">
                    {userRooms.map((room) => (
                      <div
                        key={room.roomid}
                        className={`room-item ${roomId === room.roomid ? 'selected' : ''}`}
                        onClick={() => selectRoom(room.roomid)}
                      >
                        <div className="room-item-main">
                          <div className="room-id-text">
                            {room.coupleName || room.roomid}
                          </div>
                          <div className="room-date">
                            <Calendar size={14} />
                            {formatDate(room.createdAt)}
                          </div>
                          {room.coupleName && (
                            <div className="room-id-small" title={`ルームID: ${room.roomid}`}>
                              ID: {room.roomid}
                            </div>
                          )}
                        </div>
                        <div className="room-actions">
                          {roomId === room.roomid && (
                            <div className="room-selected-indicator">
                              <Heart size={16} />
                            </div>
                          )}
                          <button
                            className="delete-room-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRoomClick(room);
                            }}
                            disabled={isDeleting}
                            title="ルームを削除"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}



          {/* 左側のカード群 */}
          <div className="left-column">
            {/* スクリーン表示セクション */}
            <div className="wedding-card screen-card">
              <div className="card-header">
                <div className="roomadmin-card-icon">
                  <Monitor size={24} />
                </div>
                <h2 className="card-title">スクリーン表示</h2>
                <p className="card-description">大画面での写真表示モードを開始します</p>
              </div>

              <div className="input-group">
                <label className="input-label">ルームID</label>
                <input
                  type="text"
                  placeholder="ルームIDを入力してください"
                  value={roomId}
                  onChange={(e) => updateRoomId(e.target.value)} // updateRoomIdを使用
                  className="wedding-input"
                />
              </div>

              <div className="screen-button-group">
                <button
                  onClick={enterScreen}
                  disabled={isLoading || !roomId.trim()}
                  className="wedding-button secondary-button screen-main-button"
                >
                  {isLoading ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <>
                      <Monitor size={20} />
                      スクリーン表示
                    </>
                  )}
                </button>


              </div>

              {screen?.msg && (
                <div className="status-message">
                  {screen.msg}
                </div>
              )}
            </div>

            {/* 検閲セクション */}
            <div className="wedding-card moderation-card">
              <div className="card-header">
                <div className="roomadmin-card-icon">
                  <Shield size={24} />
                </div>
                <h2 className="card-title">画像検閲</h2>
                <p className="card-description">写真の承認・管理を行います</p>
              </div>

              <button
                onClick={startModeration}
                disabled={!roomId}
                className="wedding-button tertiary-button"
              >
                <Shield size={20} />
                検閲を開始する
              </button>

              {moderationMsg && (
                <div className="success-message">
                  <Heart size={16} />
                  {moderationMsg}
                </div>
              )}
            </div>
          </div>

          {/* 右側のカード群 */}
          <div className="right-column">
            {/* QRコードセクション */}
            {roomResponse?.roomid && (
              <div className="wedding-card qr-card">
                <div className="card-header">
                  <div className="roomadmin-card-icon qr-icon-header">
                    <QrCode size={24} />
                  </div>
                  <h3 className="card-title">ゲスト用QRコード</h3>
                  <p className="card-description">スマートフォンでスキャンしてアクセス</p>
                </div>

                <div className="qr-actions-container">

                  <button
                    onClick={handleCreateQRPaper}
                    className="wedding-button secondary-button"
                  >
                    <FileText size={20} />
                    QRコード用紙作成
                  </button>
                </div>

                <div className="qr-code-container">
                  <div className="qr-code-frame">
                    <QRCodeCanvas
                      value={`${urlBase}/user/menu?roomid=${roomResponse.roomid}`}
                      title="Photo Memorial QR Code"
                      size={160}
                      bgColor="#ffffff"
                      fgColor="#2d1b69"
                      level="M"
                      imageSettings={{
                        src: "",
                        x: undefined,
                        y: undefined,
                        height: 24,
                        width: 24,
                        excavate: true,
                      }}
                    />
                  </div>
                  <div className="qr-decorations">
                    <div className="qr-corner qr-corner-tl"></div>
                    <div className="qr-corner qr-corner-tr"></div>
                    <div className="qr-corner qr-corner-bl"></div>
                    <div className="qr-corner qr-corner-br"></div>
                  </div>
                </div>

                <a
                  href={`${urlBase}/user/menu?roomid=${roomResponse.roomid}`}
                  className="qr-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => console.log('🔗 Click URL:', `${urlBase}/user/menu?roomid=${roomResponse.roomid}`)}
                >
                  <ExternalLink size={16} />
                  ゲストメニューページを開く
                </a>
              </div>
            )}

            {/* QRコードが表示されていない場合の空白スペース */}
            {!roomResponse?.roomid && (
              <div className="placeholder-card">
                <div className="placeholder-content">
                  <QrCode size={48} className="placeholder-icon" />
                  <p className="placeholder-text">ルームを作成すると<br />QRコードが表示されます</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="wedding-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Beautiful Memories, Elegant Management</p>
        </div>
      </div>

      {/* ルーム名入力モーダル */}
      {showRoomNameModal && (
        <div className="modal-overlay" onClick={handleModalCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-container">
                <Plus size={20} className="modal-icon" />
                <h3>新しいルームを作成</h3>
              </div>
              <button className="modal-close" onClick={handleModalCancel}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">部屋の名前</label>
                <input
                  type="text"
                  placeholder=""
                  value={modalRoomName}
                  onChange={(e) => setModalRoomName(e.target.value)}
                  onKeyPress={handleModalKeyPress}
                  maxLength={50}
                  className="wedding-input modal-input"
                  autoFocus
                />
                <div className="char-count">
                  {modalRoomName.length}/50文字
                </div>
              </div>

              <div className="modal-description">
                <Heart size={16} className="description-icon" />
                <p>ゲストの皆様が写真を共有できる専用ルームを作成します。</p>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={handleModalCancel} className="wedding-button secondary-button modal-cancel">
                キャンセル
              </button>
              <button
                onClick={handleModalConfirm}
                className="wedding-button primary-button modal-confirm"
                disabled={!modalRoomName.trim()}
              >
                <Plus size={16} />
                作成する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {showDeleteModal && deleteTargetRoom && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-container">
                <Trash2 size={20} className="modal-icon delete-icon" />
                <h3>ルームを削除</h3>
              </div>
              <button className="modal-close" onClick={handleDeleteCancel}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-warning">
                <AlertTriangle size={24} className="warning-icon" />
                <p>以下のルームを削除しますか？</p>
              </div>

              <div className="delete-target-info">
                <div className="room-name">{deleteTargetRoom.coupleName || deleteTargetRoom.roomid}</div>
                <div className="room-date">作成日: {formatDate(deleteTargetRoom.createdAt)}</div>
              </div>

              <div className="delete-notice">
                <p>⚠️ この操作は取り消せません</p>
                <p>• ルーム内のすべての写真が削除されます</p>
                <p>• ゲストは該当ルームにアクセスできなくなります</p>
              </div>

              {deleteError && (
                <div className="delete-error">
                  <AlertTriangle size={16} />
                  {deleteError}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={handleDeleteCancel} className="wedding-button secondary-button">
                キャンセル
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="wedding-button danger-button"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <Trash2 size={16} />
                    削除する
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* スクリーン設定モーダル */}
      <ScreenSettings
        roomId={roomId}
        isOpen={showScreenSettings}
        onClose={() => setShowScreenSettings(false)}
        onSettingsChanged={handleScreenSettingsChanged}
      />
    </div>
  );
};

export default RoomAdmin;
